import styled, { css } from 'styled-components';
import COLORS from '../../common/styles/color-pallete';
import DEVICES from '../../common/styles/devices';
import { FONT_FAMILY } from '../../common/styles/fontStyles';
import React from 'react';
type ButtonWrapperProps = {
  type: 'primary' | 'secondary' | 'link' | 'submit' | 'button';
  size?: 'default' | 'small';
  width?: number | string;
  height?: number;
  borderRadius?: number;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  lineHeight?: number;
  disabled?: boolean;
  imgSrc?: string;
  ref?: React.RefObject<HTMLButtonElement>;
  className?: string;
  margin?: string;
  backgroundColor?: string;
};
type ButtonImageProps = {
  src: string;
  imgPosition: 'left' | 'right';
};

type TextLinkProps = {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  lineHeight?: number;
  linkDisplay?: string;
  color?: string;
  disabled?: boolean;
  margin?: string;
};

const primaryButtonStyles = css`
  background-color: ${COLORS.brandPrimary};
  color: ${COLORS.white};

  &:hover {
    background-color: ${COLORS.brandPrimaryHover};
  }
`;

const secondaryButtonStyles = css<ButtonWrapperProps>`
  background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : 'inherit')};
  border: 2px solid ${COLORS.brandPrimary};
  color: ${COLORS.brandPrimary};

  &:hover {
    border-color: ${COLORS.brandPrimaryHover};
  }
`;
const disabledLinkStyles = css`
  color: ${COLORS.textDarkGray} !important;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
`;

const disabledStyles = css`
  background-color: ${COLORS.textDarkGray};
  color: ${COLORS.lightGrey};
  cursor: not-allowed;

  &:hover {
    background-color: ${COLORS.textDarkGray};
  }
`;

const secondaryButtonDisabledStyles = css`
  border-color: ${COLORS.textDarkGray};
  color: ${COLORS.textDarkGray};
  cursor: not-allowed;

  &:hover {
    border-color: ${COLORS.textDarkGray};
  }
`;

export const ButtonWrapper = styled.button<ButtonWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ size, width }) => {
    if (width) {
      return typeof width === 'number' ? `${width}px` : width;
    }
    return size === 'small' ? '125px' : '335px';
  }};
  height: ${({ size, height }) => (size === 'small' ? '38' : height ? height : 46)}px;
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: ${({ imgSrc, size }) => (imgSrc || size === 'small' ? '6px' : '16px')};
  padding-right: ${({ imgSrc, size }) => (imgSrc || size === 'small' ? '6px' : '16px')};
  border: none;
  border-radius: ${({ borderRadius }) => (borderRadius ? borderRadius : 4)}px;
  font-family: ${({ fontFamily }) => (fontFamily ? fontFamily : FONT_FAMILY.hellix.bold)};
  font-size: ${({ fontSize }) => (fontSize ? fontSize : 16)}px;
  line-height: ${({ lineHeight }) => (lineHeight ? lineHeight : 26)}px;
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 700)};
  margin: ${({ margin }) => (margin ? margin : 0)};
  cursor: pointer;

  ${({ type }) =>
    (type === 'primary' || type === 'button' || type === 'submit') && primaryButtonStyles}
  ${({ type }) => type === 'secondary' && secondaryButtonStyles}
  ${({ type, disabled }) =>
    disabled && (type === 'secondary' ? secondaryButtonDisabledStyles : disabledStyles)}

  @media ${DEVICES.mobileXL} {
    width: ${({ size }) => (size === 'small' ? '125px' : '100%')};
    min-height: ${({ size, height }) =>
      size === 'small' ? '38px' : `${height ? `${height}px` : '100%'}`};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

interface ButtonTextProps {
  margin?: string;
}

export const ButtonText = styled.span<ButtonTextProps>`
  margin: ${({ margin }: ButtonTextProps) => (margin ? margin : '0 8px')};
`;

export const ButtonImage = styled.img<ButtonImageProps>`
  width: 24px;
  height: 24px;
  margin-right: ${({ imgPosition }) => (imgPosition === 'left' ? '8px' : '0')};
  margin-left: ${({ imgPosition }) => (imgPosition === 'right' ? '8px' : '0')};
  float: ${({ imgPosition }) => (imgPosition === 'left' ? 'left' : 'right')};
`;

export const TextLink = styled.span<TextLinkProps>`
  cursor: pointer;
  color: ${({ color }) => (color ? color : COLORS.darkBlue)} !important;
  font-family: ${({ fontFamily }) => (fontFamily ? fontFamily : FONT_FAMILY.hellix.bold)};
  font-size: ${({ fontSize }) => (fontSize ? fontSize : 16)}px;
  line-height: ${({ lineHeight }) => (lineHeight ? lineHeight : 26)}px;
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 700)};
  display: ${({ linkDisplay }) => (linkDisplay ? 'inline-block' : 'inline')};
  margin: ${({ margin }) => (margin ? margin : 0)};

  ${({ disabled }) => disabled && disabledLinkStyles};
`;
